import React, { useEffect, useState } from "react";
import { message, Select, Spin } from "antd";
import { useDebounce } from "use-debounce";

import arrowDown from "../../../assets/icons/arrow-down12.png";
import { controller } from "../controller";

export default function ProcedureDropdown({
  insurances,
  allProcedures,
  approvedPatients,
  currentPatientID,
  currentProcedure,
  approvedProcedures,
  disabledByApproved,
  numberOfInsurances,
  setApprovedProcedures,
  isSettingProcedureCode,
  setIsSettingProcedureCode,
  setEmptyProcedureDropdown,
}) {
  const [selectedProcedure, setSelectedProcedure] = useState(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [isFilteringProcedures, setIsFilteringProcedures] = useState(false);
  const [procedureSearch, setProcedureSearch] = useState("");
  const [procedureSearchValue] = useDebounce(procedureSearch, 1000);
  const [dropdownProceduresOptions, setDropdownProceduressOptions] = useState([]);

  useEffect(function () {
    const procedureOptions = allProcedures.map((procedure) => ({
      value: procedure.id,
      label: `${procedure.procedure_code} ${procedure.procedure_code_description ? `- ${procedure.procedure_code_description}` : ""}`,
    }));

    setDropdownProceduressOptions(procedureOptions);
  }, []);

  const handleChangeProcedureSearch = async (input, option) => {
    setProcedureSearch(input); // Update the search input state
  };

  // Effect to handle debounced procedure search
  useEffect(() => {
    const fetchProcedures = async () => {
      let procedureCode = "";
      let procedureDescription = "";

      // Check if input is valid
      if (typeof procedureSearchValue !== "string" || !procedureSearchValue) {
        procedureDescription = procedureSearchValue || "";
      } else {
        // Extract numbers using regex
        const numberMatch = procedureSearchValue.match(/\d+/g);

        if (numberMatch) {
          // Join all number sequences for procedureCode
          procedureCode = numberMatch.join("");
          // Remove all numbers from input to get description
          procedureDescription = procedureSearchValue.replace(/\d+/g, "").trim();
          // Clean up any leading/trailing hyphens or spaces
          procedureDescription = procedureDescription.replace(/^-+|-+$/g, "").trim();
        } else {
          // If no numbers, entire input is description
          procedureDescription = procedureSearchValue.trim();
        }
      }

      try {
        setIsFilteringProcedures(true);
        const response = await controller.getProcedure(0, { procedureCode, procedureDescription });

        if (response.status < 250) {
          const data = await response.json;
          const procedureOptions = data.map((procedure) => ({
            value: procedure.id,
            label: `${procedure.procedure_code} ${procedure.procedure_code_description ? `- ${procedure.procedure_code_description}` : ""}`,
          }));

          setDropdownProceduressOptions(procedureOptions);
        }
      } catch (err) {
        message.error("Something went wrong while filtering procedures");
        console.error(err);
      } finally {
        setIsFilteringProcedures(false);
      }
    };

    fetchProcedures();
  }, [procedureSearchValue]);

  useEffect(
    function () {
      if (isFilteringProcedures) {
        setDropdownProceduressOptions([
          {
            value: "loading",
            label: <Spin />,
          },
        ]);
      }
    },
    [isFilteringProcedures]
  );

  // Finding the empty dropdowns for handling the approve button active or deactive
  useEffect(() => {
    setEmptyProcedureDropdown((prev) => {
      const currentTableEmptyIds = prev[currentPatientID] || [];
      // Determine if the dropdown is empty
      if (!selectedProcedure) {
        // Add currentProcedure.id to empty list if not already present
        if (!currentTableEmptyIds.includes(currentProcedure.id)) {
          return {
            ...prev,
            [currentPatientID]: [...currentTableEmptyIds, currentProcedure.id],
          };
        }
      } else {
        // Remove currentProcedure.id from empty list if present
        if (currentTableEmptyIds.includes(currentProcedure.id)) {
          return {
            ...prev,
            [currentPatientID]: currentTableEmptyIds.filter((id) => id !== currentProcedure.id),
          };
        }
      }
      return prev;
    });
  }, [selectedProcedure, currentPatientID, currentProcedure.id, setEmptyProcedureDropdown]);

  useEffect(function () {
    // Try to find the match proceudre
    const procedure = allProcedures.find((procedure) => {
      const procedureWithNoZero = procedure.procedure_code.length === 6 ? procedure.procedure_code.slice(1) : procedure.procedure_code;
      const currentProcedureWithNoZero =
        currentProcedure.procedure_code.length === 6 ? currentProcedure.procedure_code.slice(1) : currentProcedure.procedure_code;

      if (procedureWithNoZero === currentProcedureWithNoZero) {
        return procedure;
      }

      return undefined;
    });

    if (!procedure) return;

    setSelectedProcedure(procedure);
  }, []);

  // const dropdownPatientsOptions = allProcedures.map((p) => ({
  //   value: p.procedure_code,
  //   label: `${p.procedure_code} ${p.procedure_code_description ? `- ${p.procedure_code_description}` : p.name ? `- ${p.name}` : ""}`,
  // }));

  async function handleChangeProcedureCode(value) {
    const procedure = allProcedures.find((p) => p.procedure_code === value);
    setSelectedProcedure(procedure);

    try {
      setIsSettingProcedureCode(true);
      const response = await controller.setTreatmentProcedureID({ procedure_code: procedure.id }, currentProcedure.id);

      if (response.status < 250) {
        setApprovedProcedures((prevApprovedProcedures) => ({ ...prevApprovedProcedures, [currentPatientID]: response.json.updated_ids }));
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong while setting procedure code");
    } finally {
      setIsSettingProcedureCode(false);
    }
  }

  if (selectedProcedure) {
    selectedProcedure.procedure_code =
      selectedProcedure.procedure_code.length === 6 ? selectedProcedure.procedure_code.slice(1) : selectedProcedure.procedure_code;
  }

  return (
    <Select
      showSearch
      placeholder="Select Procedure"
      optionFilterProp="label"
      filterOption={(input, option) => handleChangeProcedureSearch(input, option)}
      options={dropdownProceduresOptions}
      className={`select-patient-id ${selectedProcedure && "no-transparent-background"}`}
      value={
        selectedProcedure
          ? `${selectedProcedure.procedure_code} ${
              selectedProcedure.procedure_code_description
                ? `- ${selectedProcedure.procedure_code_description} `
                : selectedProcedure.name
                ? `- ${selectedProcedure.name}`
                : ""
            }`
          : undefined
      }
      suffixIcon={<img style={{ width: 20, transform: `rotate(${isOpen ? "180deg" : "0"})` }} src={arrowDown} alt="arrow-down" />}
      onDropdownVisibleChange={(open) => setIsOpen(open)}
      onChange={handleChangeProcedureCode}
      disabled={
        approvedPatients.includes(currentPatientID) ||
        isSettingProcedureCode ||
        disabledByApproved ||
        (approvedProcedures[currentPatientID] && approvedProcedures[currentPatientID].length === numberOfInsurances) ||
        insurances.every((insurance) => insurance.is_approved === true)
      }
    />
  );
}
